import type { EndUserPages, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import type { CustomRouter } from '../../routes/useCustomRouter';
import { DATA_IMAGE_CONTAINER, DATA_MEDIA } from './enums';

/**
 * Whether the element is a video container or not.
 *
 * @param element the element.
 */
function isVideoFigure(element: Element): boolean {
  return element?.hasAttribute('data-lia-video-container');
}

/**
 * Whether the specified element is a `figure`.
 *
 * @param node the element to check.
 */
const isImageFigure = (node: Element) =>
  node &&
  node.nodeName === 'FIGURE' &&
  /image/i.test(node.className) &&
  node.hasAttribute(DATA_IMAGE_CONTAINER);

/**
 * Get anchor element within a `figure`.
 *
 * @param figureElement the figure element.
 */
function getAnchorWithinFigure(figureElement: Element): HTMLAnchorElement {
  const anchorElement = figureElement.querySelector<HTMLAnchorElement>(
    `[${DATA_IMAGE_CONTAINER}] > a[href]`
  );
  return anchorElement?.firstElementChild?.hasAttribute(DATA_MEDIA) ? anchorElement : null;
}

export const MENTIONS_CLASS = 'lia-mention';
/**
 * Whether the specified element is a `mention` element
 *
 * @param element Element to verify
 */
const isMentionsElement = (element: HTMLElement) =>
  element && element.nodeName === 'A' && element.classList.contains(MENTIONS_CLASS);

/**
 * Whether the given element is a `Spoiler` element
 *
 * @param element Element to be checked
 */
const isSpoilerElement = (element: HTMLElement) =>
  element && element.nodeName === 'A' && element.classList.contains('lia-spoiler-link');
/**
 * Whether the specified element is the image wrapper.
 *
 * @param node the element to check.
 */
const isMediaWrapperElement = (node: HTMLElement) =>
  node && node.nodeName === 'BUTTON' && node.closest('.lia-media-object');

/**
 * Whether the specified element is an external link
 *
 * @param element Element to verify
 */
const isExternalLink = (
  element: HTMLElement,
  router: CustomRouter<EndUserPages, EndUserQueryParams>
) => {
  const href = element?.getAttribute('href');
  return element && element.nodeName === 'A' && href && !router.isInternalRoute(href);
};

export {
  isVideoFigure,
  isImageFigure,
  getAnchorWithinFigure,
  isMentionsElement,
  isSpoilerElement,
  isMediaWrapperElement,
  isExternalLink
};
